import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import QRCode from "react-qr-code";
import styles from "./summary.module.scss";
import cx from "classnames";
import Header from "../Header/header";
/* ----------------------------- Types (minimal) ----------------------------- */
type Patient = {
  title?: string;
  first_name?: string;
  last_name?: string;
  email_address?: string;
  mobile_phone?: string;
  home_phone?: string;
  address_line_1?: string;
  address_line_2?: string;
  nhs_number?: string;
};

type TreatmentPlan = {
  treatments?: { description: string }[];
};

type PlaylistItem = {
  id: string | number;
  title?: string;
  thumbnail?: string;
  status?: boolean;
};

type Playlist = {
  title?: string;
  status?: boolean;
  playlist_items?: PlaylistItem[];
};

type PlaylistActivityRow = {
  video_id: string | number;
  watch_percentage: number; // 0..1
  watch_status: "Not Watched" | "In Progress" | "Completed" | string;
  last_watched?: string | null;
  has_playback_issue?: boolean;
};

type SignatureMeta = {
  signed_type?: "manual" | "digital";
  signed_timestamp?: string | null;
};

type SummaryDTO = {
  id: string | number;
  version_no: number;
  created_at: string;
  status: "Draft" | "Sent" | "Completed" | string;
  is_old_version?: boolean;
  link?: string;
  patient?: Patient;
  treatment_plan?: TreatmentPlan;
  clinician_table_id?: string | number;
  playlists?: Playlist[];
  playlist_activity?: PlaylistActivityRow[];
  patient_signature?: SignatureMeta;
};

type VersionsRow = {
  consent_bundle_id: string;
  code: string;
  version: number;
};

/* ---------------------------- Utility functions ---------------------------- */
const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toDateString() : "-";

const percent = (x: number) =>
  `${(Math.min(Math.max(x, 0), 1) * 100).toFixed(0)}%`;

/* ------------------------------- Component -------------------------------- */
export default function Summary() {
  const { consentBundleId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // UI State
  const [showQR, setShowQR] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState<VersionsRow[] | null>(null);

  // You already cache user elsewhere; keeping the pattern
  const user: any = queryClient.getQueryData(["user"]);

  /* ------------------------------- Data load ------------------------------ */
  const summaryQuery = useQuery(
    ["summaryDetails", consentBundleId],
    async () => {
      const { data } = await axios.get<SummaryDTO>(
        `/consent_bundle/${consentBundleId}/activity_summary`
      );
      // If it’s a Draft, route to edit (matches your current behavior)
      if (data.status === "Draft" && (data as any)?.data?.treatment_plan_id) {
        navigate(`/${(data as any).data.treatment_plan_id}`);
      }
      return data;
    }
  );

  const data = summaryQuery.data;

  /* ------------------------------ Mutations ------------------------------ */
  const manuallySign = useMutation(
    async (id: string | number) => {
      return axios.put(`/consent_bundle/${id}/mark_consent_bundle_as_signed`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["summaryDetails"] });
      },
    }
  );

  const createNewVersion = useMutation(
    async (id: string | number) =>
      axios.post(`/consent_bundle/${id}/create_consent_bundle_new_version`),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries({ queryKey: ["summaryDetails"] });
        const newId = (res as any)?.data?.data;
        navigate(`/new-dashboard`);
      },
    }
  );

  /* --------------------------- Action Handlers --------------------------- */
  const downloadLatest = async () => {
    if (!data) return;
    setIsDownloading(true);
    try {
      // If Completed: generate_pdf; else: generate_offline_pdf
      const completed = data.status === "Completed";
      const url = `${process.env.REACT_APP_BASE_URL}consent_bundle/${consentBundleId}/${
        completed ? "generate_pdf" : "generate_offline_pdf"
      }`;

      const resp = await axios.get(url, { responseType: "blob" });
      const href = URL.createObjectURL(resp.data);
      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", getPdfName(data));
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadFullReport = async () => {
    if (!data) return;
    setIsDownloading(true);
    try {
      const url = `${process.env.REACT_APP_BASE_URL}consent_bundle/${consentBundleId}/download_full_consent_bundle_report`;
      const resp = await axios.get(url, { responseType: "blob" });
      const href = URL.createObjectURL(resp.data);
      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", getPdfName(data));
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    } finally {
      setIsDownloading(false);
    }
  };

  const openVersions = async () => {
    try {
      const resp = await axios.get<VersionsRow[]>(
        `/consent_bundle/${consentBundleId}/get_consent_bundle_versions`
      );
      const rows = resp.data || [];
      setVersions(rows);
      setShowVersions(true);
    } catch (e) {
      setVersions(null);
      setShowVersions(true);
    }
  };

  const downloadVersion = async (bundleId: string) => {
    setIsDownloading(true);
    try {
      const url = `${process.env.REACT_APP_BASE_URL}consent_bundle/${bundleId}/generate_pdf`;
      const resp = await axios.get(url, { responseType: "blob" });
      const href = URL.createObjectURL(resp.data);
      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", getPdfName(data || ({} as any)));
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    } finally {
      setIsDownloading(false);
    }
  };

  const copyRemoteLink = () => {
    if (!data) return;
    const link = data.link || `${process.env.REACT_APP_PATIENT_URL}/${consentBundleId}`;
    navigator.clipboard.writeText(link);
  };

  /* ----------------------------- Derived bits ---------------------------- */
  const treatmentTitle = useMemo(() => {
    const list = data?.treatment_plan?.treatments || [];
    if (!list.length) return "-";
    return list.map((t) => t.description).join(", ");
  }, [data]);

  const createdOn = formatDate(data?.created_at);
  const patient = data?.patient;

  // Map video_id => activity for quick lookup
  const activityMap = useMemo(() => {
    const map = new Map<string | number, PlaylistActivityRow>();
    (data?.playlist_activity || []).forEach((row) => {
      map.set(row.video_id, row);
    });
    return map;
  }, [data]);

  const visiblePlaylists = (data?.playlists || []).filter((p) => p.status);

  const anyVideoIssue = useMemo(
    () => (data?.playlist_activity || []).some((r) => r.has_playback_issue),
    [data]
  );


const hasMultipleVersions = (data?.version_no ?? 1) > 1;
const [versionList, setVersionList] = useState<VersionsRow[]>([]);
const [openDropdown, setOpenDropdown] = useState(false);
const [showDropdown, setShowDropdown] = useState(false);


const handleVersionClick = async () => {
  if (!hasMultipleVersions) return;
  if (showDropdown) {
    setShowDropdown(false);
    return;
  }

  try {
    const resp = await axios.get<VersionsRow[]>(
      `/consent_bundle/${consentBundleId}/get_consent_bundle_versions`
    );
    setVersionList(resp.data || []);
    setShowDropdown(true);
  } catch (err) {
    console.error("Error fetching versions:", err);
    setVersionList([]);
    setShowDropdown(true);
  }
};

const handleSelectVersion = async (bundleId: string | number) => {
  setShowDropdown(false);
  // You can either navigate or download:
  navigate(`/summary/${bundleId}`);
  // or: await downloadVersion(bundleId);
};


const topActions = (
  <div className={styles.topActions}>
    <div className={styles.versionDropdownWrapper}>
      <button
        className={cx(styles.outlineBtn, {
          [styles.disabledBtn]: !hasMultipleVersions,
        })}
        onClick={handleVersionClick}
        disabled={!hasMultipleVersions}
      >
        View versions ▾
      </button>

     {showDropdown && versionList.length > 0 && (
  <ul className={styles.dropdownMenu}>
    {versionList.map((v) => (
      <li key={v.consent_bundle_id} className={styles.dropdownItem}>
        <div className={styles.versionRow}>
          <div
            className={styles.versionLabel}
            onClick={() => handleSelectVersion(v.consent_bundle_id)}
          >
            <strong>{v.code}</strong> — <span>v{v.version}</span>
          </div>

          <button
            className={styles.downloadLink}
            onClick={(e) => {
              e.stopPropagation();
              downloadVersion(v.consent_bundle_id);
            }}
            disabled={isDownloading}
          >
            download
          </button>
        </div>
      </li>
    ))}
  </ul>
)}

    </div>

    {!data?.is_old_version && (
      <button
        className={styles.outlineBtn}
        onClick={() => createNewVersion.mutate(data!.id)}
        disabled={createNewVersion.isLoading}
      >
        Edit as new version
      </button>
    )}
  </div>
);



  /* -------------------------------- Render -------------------------------- */
  return (
  <>
    <Header />
    <div className={styles.container}>
      {/* Title + Subtitle */}
      <h1 className={styles.title}>Summary</h1>
      <p className={styles.subtitle}>Here’s a summary of your patient’s journey</p>

      {/* Created Info */}
      <div className={styles.createdBox}>
        <div>
          <strong>Created on:</strong> {createdOn}
        </div>
        <div>
          <strong>Treatment plan:</strong> {treatmentTitle}
        </div>
        <span className={styles.version}>
          Version {data?.version_no ?? "-"} {topActions}
        </span>
      </div>

      {/* Patient Details */}
      <section className={styles.section}>
        <h2>Patient details</h2>
        <div className={styles.grid}>
          <div>
            <label>Name</label>
            <input
              type="text"
              readOnly
              value={
                patient
                  ? `${patient.title ?? ""} ${patient.first_name ?? ""} ${
                      patient.last_name ?? ""
                    }`.trim()
                  : "-"
              }
            />
          </div>
          <div>
            <label>Email</label>
            <input type="text" readOnly value={patient?.email_address || "-"} />
          </div>
          <div>
            <label>Mobile number</label>
            <input type="text" readOnly value={patient?.mobile_phone || "-"} />
          </div>
          <div>
            <label>Phone</label>
            <input type="text" readOnly value={patient?.home_phone || "-"} />
          </div>
          <div>
            <label>Address</label>
            <input
              type="text"
              readOnly
              value={
                patient
                  ? `${patient.address_line_1 || ""} ${
                      patient.address_line_2 || ""
                    }`.trim() || "-"
                  : "-"
              }
            />
          </div>
          <div>
            <label>NHS Number</label>
            <input type="text" readOnly value={patient?.nhs_number || "-"} />
          </div>
        </div>
      </section>

      {/* Video + Consent row */}
      <div className={styles.row}>
        {/* Video status */}
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>Video status</h3>

          {anyVideoIssue && (
            <div className={styles.issuesSection}>
              <p className={styles.issueBox}>User Reported an Issue with Video</p>
            </div>
          )}

          {visiblePlaylists.length ? (
            <div className={styles.playlists}>
              {visiblePlaylists.map((playlist, pIdx) => (
                <div className={styles.playlistBlock} key={`pl-${pIdx}`}>
                  <div className={styles.playlistHeader}>
                    {playlist.title || `Playlist ${pIdx + 1}`}
                  </div>
                  <div className={styles.videos}>
                    {(playlist.playlist_items || [])
                      .filter((v) => v.status)
                      .map((video, vIdx) => {
                        const act = activityMap.get(video.id) || {
                          watch_percentage: 0,
                          watch_status: "Not Watched",
                        };
                        return (
                          <div className={styles.videoRow} key={`v-${vIdx}`}>
                            <div className={styles.videoDetailsColumn}>
                              {video.thumbnail ? (
                                <img
                                  className={styles.thumbnail}
                                  src={video.thumbnail}
                                  alt={video.title || "Video"}
                                />
                              ) : (
                                <div className={styles.thumbnailPlaceholder} />
                              )}

                              <div className={styles.videoDetails}>
                                <p className={styles.videoTitle}>
                                  {video.title || "Untitled video"}
                                </p>
                                <p
                                  className={cx(styles.statusPill, {
                                    [styles.ok]:
                                      act.watch_percentage >= 0.8 ||
                                      act.watch_status.toLowerCase() ===
                                        "completed",
                                    [styles.warn]:
                                      act.watch_percentage > 0 &&
                                      act.watch_percentage < 0.8 &&
                                      act.watch_status.toLowerCase() !==
                                        "completed",
                                    [styles.err]: act.watch_percentage === 0,
                                  })}
                                >
                                  {act.watch_status} ({percent(act.watch_percentage)})
                                </p>
                                <p className={styles.lastAccessed}>
                                  Last Accessed: {act.last_watched || "-"}
                                </p>
                              </div>
                            </div>

                            {act.has_playback_issue && (
                              <div className={styles.issueRight}>
                                <span className={styles.issueBoxSmall}>
                                  Reported playback issue
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className={styles.muted}>
              No video logs to display in the summary page
            </p>
          )}
          <p className={styles.muted}>
            Adding media to your consent form increases your patient confidence level in their
            treatment plan.
          </p>
        </div>

        {/* Consent status */}
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>Consent status</h3>

          <div className={styles.statusBadges}>
            <span
              className={cx(styles.badge, {
                [styles.badgeGreen]: data?.status === "Completed",
                [styles.badgeOrange]: data?.status !== "Completed",
              })}
            >
              {data?.status || "—"}
            </span>
          </div>

          {data?.status === "Completed" ? (
            <>
              {data?.patient_signature?.signed_type === "manual" ? (
                <p className={styles.muted}>Signed manually at the practice</p>
              ) : (
                <p className={styles.muted}>
                  Signed on: {formatDate(data?.patient_signature?.signed_timestamp)}
                </p>
              )}
              <button
                className={styles.primaryBtn}
                onClick={downloadLatest}
                disabled={isDownloading}
              >
                Download latest consent
              </button>

              {data?.version_no && data.version_no > 1 && (
                <button
                  className={cx(styles.outlineBtn, styles.spacedTop)}
                  onClick={downloadFullReport}
                  disabled={isDownloading}
                >
                  Download the full consent report
                </button>
              )}
            </>
          ) : (
            <>
              <div className={styles.fieldRow}>
                <label>Signed manually at the practice</label>
                <button
                  className={cx(styles.outlineBtn, styles.full)}
                  disabled={manuallySign.isLoading}
                  onClick={() => manuallySign.mutate(data!.id)}
                >
                  Mark as signed manually
                </button>
              </div>

              <div className={styles.fieldRow}>
                <label>Clinician signature</label>
                <span className={styles.badgePink}>
                  {data?.patient_signature?.signed_timestamp
                    ? "APPLIED"
                    : "NOT SIGNED"}
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      
      {data?.status !== "Completed" && (
        <>
          {/* Sign remotely */}
          <div className={styles.cardWide}>
            <h3 className={styles.cardTitle}>Sign remotely</h3>
            <p>
              We’ve provided your patient with a secure link to view and complete their
              consent documents. If you’d like to resend the form manually via email or text,
              simply copy the link below.
            </p>
            <p className={styles.linkText}>
              {data?.link || `${process.env.REACT_APP_PATIENT_URL}/${consentBundleId}`}
            </p>
            <button className={styles.outlineBtn} onClick={copyRemoteLink}>
              Copy link
            </button>
          </div>

          {/* Sign in room + manual printing */}
          <div className={styles.row}>
            <div className={styles.card}>
              <h3 className={styles.cardTitle}>Sign in waiting room</h3>
              <p>
                Generate a QR code to display in your waiting room. When scanned, your patient
                will be directed to their consent documents.
              </p>
              <button className={styles.outlineBtn} onClick={() => setShowQR(true)}>
                Generate QR code
              </button>

              {showQR && (
                <div
                  className={styles.qrModalBackdrop}
                  onClick={() => setShowQR(false)}
                >
                  <div
                    className={styles.qrModal}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <QRCode
                      value={
                        data?.link ||
                        `${process.env.REACT_APP_PATIENT_URL}/${consentBundleId}`
                      }
                    />
                    <button
                      className={styles.outlineBtn}
                      onClick={() => setShowQR(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className={styles.card}>
              <h3 className={styles.cardTitle}>Manual consent form printing</h3>
              <p>
                Prefer a paper copy? You can print the consent form and present it to your
                patient in person.
              </p>
              <button
                className={styles.outlineBtn}
                onClick={downloadLatest}
                disabled={isDownloading}
              >
                Print consent form
              </button>
            </div>
          </div>
        </>
      )}

      {/* Footer */}
      <div className={styles.footer}>
        <button className={styles.outlineBtn} onClick={() => navigate("/new-dashboard")}>
          Go to dashboard
        </button>
      </div>
    </div>
  </>
);

}

/* -------------------------- Local helper (name) -------------------------- */
// Kept local to avoid importing from your utils; adjust if you prefer.
function getPdfName(s: SummaryDTO | any) {
  const patient = s?.patient;
  const name = patient
    ? `${patient.first_name || ""}_${patient.last_name || ""}`.replace(/\s+/g, "_")
    : "patient";
  const ver = s?.version_no ? `v${s.version_no}` : "v1";
  return `consent_${name}_${ver}.pdf`;
}
